import { computed, ref, unref, watch } from 'vue';
import InboxTemplateFoldersAPI from 'dashboard/api/inboxTemplateFolders';

export const ALL_FOLDERS = 'ALL';
export const UNFILED_FOLDER = 'UNFILED';
export const FOLDER_NAME_MAX_LENGTH = 80;

export const templateLinkKey = (name, language) =>
  `${name || ''}\u0000${language || ''}`;

const sortFolders = folders =>
  [...folders].sort((left, right) =>
    String(left.name || '').localeCompare(String(right.name || ''), undefined, {
      sensitivity: 'base',
    })
  );

const normalizeList = value => (Array.isArray(value) ? value : []);

export function useTemplateFolders(inboxIdSource) {
  const folders = ref([]);
  const links = ref([]);
  const canManage = ref(false);
  const foldersAvailable = ref(false);
  const isLoading = ref(false);
  const loadError = ref(false);
  const selectedFolder = ref(ALL_FOLDERS);
  const requestSeq = ref(0);

  const inboxId = computed(() => unref(inboxIdSource));

  const linkMap = computed(() => {
    const map = new Map();
    links.value.forEach(link => {
      map.set(
        templateLinkKey(link.template_name, link.language),
        link.folder_id ?? null
      );
    });
    return map;
  });

  const folderById = computed(() => {
    const map = new Map();
    folders.value.forEach(folder => {
      map.set(folder.id, folder);
    });
    return map;
  });

  const beginRequest = () => ({
    inboxId: inboxId.value,
    seq: requestSeq.value,
  });

  const isCurrent = token =>
    token.seq === requestSeq.value && token.inboxId === inboxId.value;

  const folderIdFor = template => {
    const folderId = linkMap.value.get(
      templateLinkKey(template?.name, template?.language)
    );
    if (folderId == null) return null;
    return folderById.value.has(folderId) ? folderId : null;
  };

  const folderNameFor = template => {
    const folderId = folderIdFor(template);
    if (folderId == null) return null;
    return folderById.value.get(folderId)?.name || null;
  };

  const matchesFolder = (template, folderFilter = selectedFolder.value) => {
    if (folderFilter === ALL_FOLDERS) return true;
    const folderId = folderIdFor(template);
    if (folderFilter === UNFILED_FOLDER) return folderId == null;
    return folderId === folderFilter;
  };

  const applyListPayload = data => {
    folders.value = sortFolders(normalizeList(data?.folders));
    links.value = normalizeList(data?.links).map(link => ({
      template_name: link.template_name,
      language: link.language,
      folder_id: link.folder_id ?? null,
    }));
    canManage.value = Boolean(data?.can_manage);
    foldersAvailable.value = true;
    loadError.value = false;
    if (
      selectedFolder.value !== ALL_FOLDERS &&
      selectedFolder.value !== UNFILED_FOLDER &&
      !folderById.value.has(selectedFolder.value)
    ) {
      selectedFolder.value = ALL_FOLDERS;
    }
  };

  const resetState = ({ keepError = false } = {}) => {
    folders.value = [];
    links.value = [];
    canManage.value = false;
    foldersAvailable.value = false;
    isLoading.value = false;
    if (!keepError) loadError.value = false;
    selectedFolder.value = ALL_FOLDERS;
  };

  const loadFolders = async () => {
    const currentInboxId = inboxId.value;
    if (!currentInboxId) {
      requestSeq.value += 1;
      resetState();
      return;
    }

    const seq = requestSeq.value + 1;
    requestSeq.value = seq;
    const token = { inboxId: currentInboxId, seq };
    isLoading.value = true;
    loadError.value = false;

    try {
      const { data } = await InboxTemplateFoldersAPI.list(currentInboxId);
      if (!isCurrent(token)) return;
      applyListPayload(data);
    } catch {
      if (!isCurrent(token)) return;
      resetState({ keepError: true });
      loadError.value = true;
    } finally {
      if (isCurrent(token)) isLoading.value = false;
    }
  };

  const upsertFolder = folder => {
    folders.value = sortFolders([
      ...folders.value.filter(item => item.id !== folder.id),
      { id: folder.id, name: folder.name },
    ]);
  };

  const upsertLink = link => {
    const key = templateLinkKey(link.template_name, link.language);
    const next = links.value.filter(
      item => templateLinkKey(item.template_name, item.language) !== key
    );
    if (link.folder_id != null) {
      next.push({
        template_name: link.template_name,
        language: link.language,
        folder_id: link.folder_id,
      });
    }
    links.value = next;
  };

  const createFolder = async name => {
    const token = beginRequest();
    const { data } = await InboxTemplateFoldersAPI.createFolder(
      token.inboxId,
      name
    );
    if (!isCurrent(token)) return { ignored: true };
    if (data?.folder) upsertFolder(data.folder);
    return { ignored: false, folder: data?.folder };
  };

  const renameFolder = async (folderId, name) => {
    const token = beginRequest();
    const { data } = await InboxTemplateFoldersAPI.renameFolder(
      token.inboxId,
      folderId,
      name
    );
    if (!isCurrent(token)) return { ignored: true };
    if (data?.folder) upsertFolder(data.folder);
    return { ignored: false, folder: data?.folder };
  };

  const deleteFolder = async folderId => {
    const token = beginRequest();
    await InboxTemplateFoldersAPI.deleteFolder(token.inboxId, folderId);
    if (!isCurrent(token)) return { ignored: true };
    folders.value = folders.value.filter(folder => folder.id !== folderId);
    links.value = links.value.filter(link => link.folder_id !== folderId);
    if (selectedFolder.value === folderId) selectedFolder.value = ALL_FOLDERS;
    return { ignored: false };
  };

  const assignTemplate = async ({ templateName, language, folderId }) => {
    const token = beginRequest();
    const { data } = await InboxTemplateFoldersAPI.assignTemplate(
      token.inboxId,
      {
        templateName,
        language,
        folderId,
      }
    );
    if (!isCurrent(token)) return { ignored: true };
    upsertLink({
      template_name: data?.template_name ?? templateName,
      language: data?.language ?? language,
      folder_id: data?.folder_id ?? null,
    });
    return { ignored: false, link: data };
  };

  const selectFolder = key => {
    selectedFolder.value = key;
  };

  watch(
    inboxId,
    (currentId, previousId) => {
      if (currentId === previousId) return;
      requestSeq.value += 1;
      resetState();
      if (currentId) loadFolders();
    },
    { immediate: true }
  );

  return {
    folders,
    links,
    canManage,
    foldersAvailable,
    isLoading,
    loadError,
    selectedFolder,
    folderIdFor,
    folderNameFor,
    matchesFolder,
    loadFolders,
    createFolder,
    renameFolder,
    deleteFolder,
    assignTemplate,
    selectFolder,
  };
}
